// frontend/src/services/api.js - UPDATED with course creation
import axios from 'axios';
import { auth } from '../config/firebase';
import { streamSSE } from './streaming';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 100000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add Firebase token to all requests
api.interceptors.request.use(async (config) => {
  try {
    const user = auth.currentUser;
    if (user) {
      const token = await user.getIdToken();
      config.headers['Authorization'] = `Bearer ${token}`;
    } else {
      // Development fallback - remove in production
      config.headers['X-User-Id'] = 'anonymous';
    }
  } catch (error) {
    console.error('Error getting auth token:', error);
    // Continue without auth for now
  }
  
  return config;
});

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('API Error:', error.response?.data || error.message);
    
    // Handle auth errors
    if (error.response?.status === 401) {
      // Token expired or invalid - Firebase will handle re-auth
      console.log('Authentication error - user may need to sign in again');
    }
    
    return Promise.reject(error);
  }
);

export const apiService = {
  // Health check
  health: () => api.get('/health'),

  // Course endpoints
  getCourse: (courseCode = 'CKA') => api.get(`/api/v1/courses/${courseCode}`),
  
  // NEW: Create course endpoint
  createCourse: (courseData) => api.post('/api/v1/courses', courseData),
  
  // NEW: List user's courses (for future use)
  getUserCourses: () => api.get('/api/v1/courses/my-courses'),
  deleteCourse: (courseId) => api.delete(`/api/v1/courses/${courseId}`),
  
  // Add to api.js
  generateCourseStructure: (prompt) => 
    api.post('/api/v1/courses/generate', { prompt }),

  // Progress endpoints
  getProgressOverview: (courseId = 'BASICW') => 
    api.get(`/api/v1/progress/overview?course_id=${courseId}`),
  
  getQuickStats: (courseId = 'BASICW') => 
    api.get(`/api/v1/progress/stats?course_id=${courseId}`),
  
  getDomainProgress: (domain, courseId = 'BASICW') => 
    api.get(`/api/v1/progress/domain/${domain}?course_id=${courseId}`),
  
  getAllDomainsProgress: (courseId = 'BASICW') => 
    api.get(`/api/v1/progress/domains?course_id=${courseId}`),

  // Question endpoints
  generateQuestion: (domain, courseId = 'BASICW', focusConcept = null) => 
    api.post('/api/v1/questions/generate', {
      course_id: courseId,
      domain,
      focus_concept: focusConcept
    }),
  
  evaluateAnswer: (questionId, userAnswer, timeTaken, courseId = 'BASICW') => 
    api.post('/api/v1/questions/evaluate', {
      course_id: courseId,
      question_id: questionId,
      user_answer: userAnswer,
      time_taken: timeTaken
    }),

  // Quiz endpoints
  //
  // generateQuiz transparently handles two backend response shapes:
  //   - 201 + QuizResponse: returned synchronously by the static template path
  //   - 202 + {job_id, status, progress}: agentic multi-agent path that takes
  //     30-60s. We poll /quiz/jobs/:id every 2s until status="done".
  //
  // The caller always gets back a regular axios-shaped {data: QuizResponse}
  // so existing components don't need to change. onProgress is optional: if
  // provided it's called with each poll's progress dict so the UI can show
  // "curator picking slots…" / "3 of 5 questions ready" etc.
  generateQuiz: async (domain, onProgress, quizLength = 5, courseId = 'BASICW') => {
    const res = await api.post('/api/v1/quiz/generate', {
      course_id: courseId,
      ...(domain && { domain }),
      quiz_length: quizLength,
    });
    if (res.status !== 202) return res;  // 201 sync — done

    const { job_id: jobId } = res.data;
    if (onProgress) onProgress(res.data.progress || {});

    // Poll. Cap at ~3 minutes total — well past the worst case.
    const POLL_MS = 2000;
    const MAX_POLLS = 90;
    for (let i = 0; i < MAX_POLLS; i++) {
      await new Promise((r) => setTimeout(r, POLL_MS));
      const job = await api.get(`/api/v1/quiz/jobs/${jobId}`);
      if (onProgress) onProgress(job.data.progress || {});
      if (job.data.status === 'done' && job.data.quiz) {
        return { ...job, data: job.data.quiz, status: 201 };
      }
      if (job.data.status === 'failed') {
        throw new Error(job.data.error || 'Quiz generation failed');
      }
    }
    throw new Error('Quiz generation timed out');
  },

  getQuizJob: (jobId) => api.get(`/api/v1/quiz/jobs/${jobId}`),

  evaluateQuiz: (quizId, userAnswers, timesTaken, courseId = 'BASICW') => 
    api.post('/api/v1/quiz/evaluate', {
      course_id: courseId,
      quiz_id: quizId,
      user_answers: userAnswers,
      times_taken: timesTaken
    }),
  
  getQuiz: (quizId) => api.get(`/api/v1/quiz/${quizId}`),

  // Abandon quiz
  abandonQuiz: (quizId) => api.post(`/api/v1/quiz/${quizId}/abandon`),

  getContentStatus: (courseId) =>
    api.get(`/api/v1/content/status/${courseId}`),

  // Agentic content editor — user instruction + current draft → AI rewrite.
  // Returns { new_content, summary, tools_used }. Caller decides whether to save.
  refineContent: ({ courseId, domain, concept, contentType, instruction, currentContent }) =>
    api.post('/api/v1/content/refine', {
      course_id: courseId,
      domain,
      concept,
      content_type: contentType,
      instruction,
      current_content: currentContent || '',
    }),

  // Tutor / onboarding agent. Each call returns the full session view
  // ({ session_id, status, messages[], proposal? }) so the chat UI has
  // one render path regardless of which endpoint produced it.
  // startTutor: opens the tutor. Accepts an optional opener message
  // (chip from landing, first user turn) AND an optional surface
  // context ({course_code, domain, concept, surface}) so the agent
  // can open with a focused suggestion when the user came from a
  // specific course/concept page.
  startTutor: (initialMessage, context) =>
    api.post('/api/v1/tutor/start', {
      ...(initialMessage ? { initial_message: initialMessage } : {}),
      ...(context ? { context } : {}),
    }),
  // Streaming SSE — invokes onEvent per chunk. Returns { promise, abort }
  // so the caller can cancel. See `frontend/src/services/streaming.js`
  // for wire format. The non-streaming `POST /tutor/message` route was
  // removed once useTutorRuntime fully owned the chat surface.
  streamTutorMessage: ({ sessionId, text, context, onEvent }) =>
    streamSSE({
      path: '/api/v1/tutor/message/stream',
      body: {
        session_id: sessionId,
        text,
        ...(context ? { context } : {}),
      },
      onEvent,
    }),
  getTutorSession: (sessionId) =>
    api.get(`/api/v1/tutor/sessions/${sessionId}`),
  // End any active tutor session — next start yields a fresh thread.
  // Wired to the panel header's "Start fresh" button.
  endActiveTutor: () => api.post('/api/v1/tutor/end-active'),
  // Wipe pending_action / proposal on the session sidecar. Wired to
  // the propose_action button click — without this, the button
  // persists across refresh because /tutor/start hydrates panel
  // state from the (still-set) capture.
  clearTutorCapture: (sessionId) =>
    api.post(`/api/v1/tutor/sessions/${encodeURIComponent(sessionId)}/clear-capture`),

  // Apply a tutor-proposed curriculum edit. The tutor stashes the
  // proposal in capture['pending_curriculum_edit']; the panel renders
  // the EditProposalCard; click Apply → this call. Server re-validates
  // the per-kind payload before mutating the course.
  applyCurriculumEdit: (courseCode, { kind, payload, expectedUpdatedAt }) =>
    api.post(`/api/v1/courses/${encodeURIComponent(courseCode)}/apply-edit`, {
      kind,
      payload,
      ...(expectedUpdatedAt ? { expected_updated_at: expectedUpdatedAt } : {}),
    }),

  // Concept drafts (tutor-generated, ephemeral). Save commits to
  // ConceptContent; discard deletes the draft. See
  // backend/app/api/routes/draft_endpoints.py.
  commitDraft: (draftId) =>
    api.post(`/api/v1/content/drafts/${encodeURIComponent(draftId)}/commit`),
  discardDraft: (draftId) =>
    api.delete(`/api/v1/content/drafts/${encodeURIComponent(draftId)}`),
  // Pending-draft summary for the Opener "you left a draft" line (#77).
  // Read-only: { count, latest: { concept, course_id, age_minutes,
  // expires_in_minutes } | null }.
  getPendingDrafts: () => api.get('/api/v1/content/drafts'),

  // Get all content for a domain
  getDomainContent: (courseId, domain, contentType = 'material') => 
    api.get(`/api/v1/content/domain/${courseId}/${domain}?content_type=${contentType}`),

  // Get specific concept content
  getConceptContent: (courseId, domain, concept, contentType = 'material') => 
    api.get(`/api/v1/content/${courseId}/${domain}/${concept}?content_type=${contentType}`),

  // Save concept content
  saveConceptContent: (data) => 
    api.post('/api/v1/content/save', data),

  // Generate AI content for a concept
  generateConceptContent: (data) => 
    api.post('/api/v1/content/generate', data),

  // Bulk operations
  generateDomainContent: (courseId, domain, contentType = 'material') =>
    api.post('/api/v1/content/generate-domain', {
      course_id: courseId,
      domain,
      content_type: contentType
    }),

  generateCourseContent: (courseId, contentType = 'material') =>
    api.post('/api/v1/content/generate-course', {
      course_id: courseId,
      content_type: contentType
    }),

  // Content export
  exportCourseContent: (courseId, format = 'markdown') =>
    api.get(`/api/v1/content/export/${courseId}?format=${format}`),

  // Content search (for future use)
  searchContent: (courseId, query) =>
    api.get(`/api/v1/content/search/${courseId}?q=${encodeURIComponent(query)}`),

  // =====================================================================
  // Mastery dashboard
  // =====================================================================
  getCoursesSummary: () => api.get('/api/v1/progress/courses-summary'),

  getMasteryDashboard: (courseId) =>
    api.get(`/api/v1/progress/mastery?course_id=${courseId}`),

  getLearningStreak: (courseId) =>
    api.get(`/api/v1/progress/streak?course_id=${courseId}`),

  getStudyPlan: (courseId) =>
    api.get(`/api/v1/progress/study-plan?course_id=${courseId}`),

  getLearningInsights: (courseId) =>
    api.get(`/api/v1/progress/insights?course_id=${courseId}`),

  // =====================================================================
  // Workbench (repair sessions — the sandbox successor)
  // =====================================================================
  startWorkbench: (courseId, domain) =>
    api.post('/api/v1/workbench/start', { course_id: courseId, domain }),

  getWorkbench: (sessionId) =>
    api.get(`/api/v1/workbench/${sessionId}`),

  saveWorkbenchFile: (sessionId, path, content) =>
    api.put(`/api/v1/workbench/${sessionId}/file`, { path, content }),

  checkWorkbench: (sessionId) =>
    api.post(`/api/v1/workbench/${sessionId}/check`),

  submitWorkbench: (sessionId) =>
    api.post(`/api/v1/workbench/${sessionId}/submit`),

  abandonWorkbench: (sessionId) =>
    api.post(`/api/v1/workbench/${sessionId}/abandon`),

  // =====================================================================
  // Question feedback
  // =====================================================================
  reportQuestion: (questionId, reason, details = '') =>
    api.post('/api/v1/feedback/report', {
      question_id: questionId,
      reason,
      details,
    }),

  // =====================================================================
  // Tenant management
  // =====================================================================
  createTenant: (name, slug, description = null) =>
    api.post('/api/v1/tenants', { name, slug, description }),

  getMyTenants: () => api.get('/api/v1/tenants/my-tenants'),

  getCurrentTenant: () => api.get('/api/v1/tenants/current'),

  addTenantMember: (userId, role = 'member') =>
    api.post('/api/v1/tenants/members', { user_id: userId, role }),

  getTenantMembers: () => api.get('/api/v1/tenants/members'),
};

export const handleApiError = (error) => {
  const data = error.response?.data;
  // Pydantic-style field errors: prefer the first specific message — it's
  // far more useful than the generic "Request validation failed" detail.
  if (Array.isArray(data?.errors) && data.errors.length > 0) {
    const e0 = data.errors[0];
    if (e0?.message) return e0.message;
  }
  if (data?.detail) {
    if (typeof data.detail === 'string') return data.detail;
    if (Array.isArray(data.detail) && data.detail[0]?.msg) return data.detail[0].msg;
  }
  if (data?.message) return data.message;
  return error.message || 'An unexpected error occurred';
};

export default api;